import { fabric } from "fabric";
import { ICanvasOptions, IObjectOptions, Object as FabricObject } from "fabric/fabric-impl";
import { CanvasData, Extension, FabricData, isObjectExtension, ObjectExtension, Textbox } from "../types/canvasTypes";
import { triggerObjectModifiedEvent } from "../helpers/canvasHelpers";
import { ADDITIONAL_CANVAS_FIELDS } from "../constants/canvasConstants";
import { ExtendedObject } from "../types/objectTypes";
import { initTextOnCanvas, isText } from "../helpers/textHelpers";
import { isBackgroundImage } from "./shapeUtils";
import { initBackgroundImageOnCanvas } from "./backgroundUtils";

type Canvas = ReturnType<typeof createFabricCanvas>;
export function findObjectExtension(name: string, extensions: Extension[]) {
  return extensions.find((extension) => extension.name === name && isObjectExtension(extension)) as
    | ObjectExtension
    | undefined;
}
export function createFabricCanvas(element: HTMLCanvasElement | string | null, options?: ICanvasOptions) {
  return new fabric.Canvas(element, options);
}

export function createStaticCanvas(width: number, height: number) {
  return new fabric.StaticCanvas(null, {
    width,
    height,
  });
}

export function modifyActiveObject(canvas: Canvas, options: IObjectOptions) {
  const object = canvas?.getActiveObject();

  if (!object) return;

  modifyObject(canvas, object, options);
}

export function modifyObject(canvas: Canvas, object: FabricObject, options: IObjectOptions) {
  object.setOptions(options);
  canvas.renderAll();
  triggerObjectModifiedEvent(canvas);
}

export function initControls(obj: FabricObject) {
  obj.setOptions({
    cornerColor: "white",
    cornerStrokeColor: "#ffa66b",
    cornerStyle: "rect",
    cornerSize: 6,
    borderColor: "#ffa66b",
    transparentCorners: false,
  });

  obj.controls.mtr.offsetY = -10;
}

export function deselectAll(canvas: Canvas) {
  canvas.discardActiveObject().renderAll();
}

export function deleteActiveObjects(canvas: Canvas) {
  const objects = canvas.getActiveObjects();

  canvas.discardActiveObject();
  objects.forEach((object) => canvas.remove(object));
}

export function addObjectToCanvas(canvas: Canvas, obj: FabricObject, options: { isCentered?: boolean } = {}) {
  canvas.add(obj);

  if (options.isCentered) {
    canvas.viewportCenterObject(obj);
  }

  canvas.setActiveObject(obj);
}

export function calculateObjectWidthConsideringScale(obj: { width?: number; scaleX?: number }) {
  if (obj.width === undefined || obj.scaleX === undefined) return 0;

  return Math.round(obj.width * obj.scaleX);
}

export function calculateObjectHeightConsideringScale(obj: { height?: number; scaleY?: number }) {
  if (obj.height === undefined || obj.scaleY === undefined) return 0;

  return Math.round(obj.height * obj.scaleY);
}

export function getCanvasJsonData(canvas: Canvas) {
  return canvas.toJSON(Object.values(ADDITIONAL_CANVAS_FIELDS));
}

export function getObjectJsonData<T = FabricObject>(object: FabricObject) {
  return object.toJSON(Object.values(ADDITIONAL_CANVAS_FIELDS)) as unknown as T;
}

export function findViewportObjectInFabricJson(data: FabricData) {
  return data.objects.find((obj) => (obj as ExtendedObject).meaning === "viewport") as ExtendedObject | undefined;
}

export function findBackgroundImageObjectInFabricJson(data: FabricData) {
  return data.objects.find((obj) => (obj as ExtendedObject).meaning === "background-image") as
    | ExtendedObject
    | undefined;
}

export async function loadImage(url: string): Promise<fabric.Image> {
  return new Promise((resolve) => {
    fabric.Image.fromURL(
      url,
      (image) => {
        resolve(image);
      },
      { crossOrigin: "anonymous" }
    );
  });
}
export function processNewObjectOnCanvas(obj: ExtendedObject) {
  if (isText(obj)) {
    initTextOnCanvas(obj);
  } else if (isBackgroundImage(obj)) {
    initBackgroundImageOnCanvas(obj);
  }

  initControls(obj);
}

export function modifyScaleIfNeeded(obj: FabricObject, newWidth?: number, newHeight?: number) {
  if (obj.width && newWidth) {
    obj.set("scaleX", newWidth / obj.width);
  }
  if (obj.height && newHeight) {
    obj.set("scaleY", newHeight / obj.height);
  }
}

export function modifyScaleSavingAspectRatioIfNeeded(obj: FabricObject, newWidth?: number, newHeight?: number) {
  if (newWidth !== undefined) {
    modifyScaleSavingOriginalAspectRatioByWidth(obj, newWidth);
  }
  if (newHeight !== undefined) {
    modifyScaleSavingOriginalAspectRatioByHeight(obj, newHeight);
  }
}

export function modifyScaleSavingOriginalAspectRatioByWidth(obj: FabricObject, width: number) {
  const originalWidth = obj.width;
  if (!originalWidth) return;

  const scale = width / originalWidth;

  obj.set({
    scaleY: scale,
    scaleX: scale,
  });
}

export function modifyScaleSavingOriginalAspectRatioByHeight(obj: FabricObject, height: number) {
  const originalHeight = obj.height;
  if (!originalHeight) return;

  const scale = height / originalHeight;

  obj.set({
    scaleY: scale,
    scaleX: scale,
  });
}

export function modifyScaleSavingCurrentAspectRatioByWidth(obj: FabricObject, width: number) {
  const originalWidth = obj.width;

  if (!originalWidth) return;

  const scaleX = width / originalWidth;
  const scaleCoefficient = obj.scaleX! / scaleX;
  const scaleY = obj.scaleY! / scaleCoefficient;

  obj.set({
    scaleX,
    scaleY,
  });
}

export function modifyScaleSavingCurrentAspectRatioByHeight(obj: FabricObject, height: number) {
  const originalHeight = obj.height;

  if (!originalHeight) return;

  const scaleY = height / originalHeight;
  const scaleCoefficient = obj.scaleY! / scaleY;
  const scaleX = obj.scaleX! / scaleCoefficient;

  obj.set({
    scaleX,
    scaleY,
  });
}

export function getProcessedCoordinatesAndDimensionsFromFabricObject(obj: FabricObject) {
  const width = calculateObjectWidthConsideringScale(obj);
  const height = calculateObjectHeightConsideringScale(obj);
  let top = Math.trunc(obj.top as number);
  let left = Math.trunc(obj.left as number);

  if (obj.originX === "center" && obj.originY === "center") {
    top = Math.trunc(obj.top! - height / 2);
    left = Math.trunc(obj.left! - width / 2);
  }

  return {
    width,
    height,
    top,
    left,
  };
}

export function getProcessedCoordinatesFromStoreForCenteredObject({
  top,
  left,
  width,
  height,
}: {
  top: number;
  left: number;
  width: number;
  height: number;
}) {
  return {
    top: top + height / 2,
    left: left + width / 2,
  };
}

export function getProcessedFontSizeFromFabricObject(obj: Textbox) {
  return Math.trunc(obj.fontSize! * Math.max(obj.scaleX!, obj.scaleY!));
}

export function getObjectPosition(object: FabricObject) {
  return {
    top: object.get("top") ?? 0,
    left: object.get("left") ?? 0,
  };
}

export function isObjectPartOfGroup(obj: FabricObject) {
  return !!obj.group;
}

export function isObjectsWithEqualMeanings(obj1?: FabricObject, obj2?: FabricObject) {
  return (
    obj1 &&
    obj2 &&
    "meaning" in obj1 &&
    "meaning" in obj2 &&
    (obj1 as ExtendedObject).meaning === (obj2 as ExtendedObject).meaning
  );
}

export function processLoadedObjects(canvas: Canvas) {
  canvas.getObjects().forEach((obj: ExtendedObject) => {
    processNewObjectOnCanvas(obj);
  });
}

export function setDefaultUniScaleKey(canvas: Canvas) {
  // Default value http://fabricjs.com/docs/fabric.Canvas.html#uniScaleKey
  canvas.uniScaleKey = "shiftKey";
}

export function disableUniScaleKey(canvas: Canvas) {
  canvas.uniScaleKey = "none";
}

export interface DynamicTextInfo {
  object: ExtendedObject;
  dynamicValues: string[];
  indexInCanvasData: number;
}

export function extractDynamicTextFromCanvasData(data: CanvasData): DynamicTextInfo[] {
  return data.objects.reduce<DynamicTextInfo[]>((acc, obj, index) => {
    if (isText(obj) && obj.text) {
      const dynamicValues = extractDynamicValuesFromText(obj.text);

      if (dynamicValues.length > 0) {
        acc.push({
          object: obj as ExtendedObject,
          dynamicValues,
          indexInCanvasData: index,
        });
      }
    }
    return acc;
  }, []);
}

const extractDynamicValuesFromText = (text: string): string[] => {
  const dynamicValueRegex = /\{([^}]+)\}/g;
  const matches = text.match(dynamicValueRegex);
  return matches ? matches : [];
};

export const removeMustache = (text: string): string => {
  return text.replace("{", "").replace("}", "");
};
